/**
 * @file 图表浮窗交互 Hook：hover 自动关闭、浮层内操作检测、外点关闭、游戏下拉 inert 搜索框。
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { RegionSelectValueType } from '@home/components/NewRegionSelect/type';
import { useDismissOutside } from '../../hooks/useDismissOutside';
import {
  CHART_POPOVER_AUTO_CLOSE_DELAY_MS,
  CHART_POPOVER_INSIDE_CLOSEST,
  CHART_POPOVER_INSIDE_SELECTORS,
} from './chartPopoverConstants';

/** useChartPopoverInteraction 入参 */
interface IUseChartPopoverInteractionParams {
  /** 浮窗是否已打开 */
  opened: boolean;
  /** 关闭浮窗回调 */
  closePanel: () => void;
  /** useHover 检测到的触发器 hover 态 */
  hovered: boolean;
  /** 触发器 hover 实时 ref，与 useHover effect 同步 */
  hoveredRef: React.MutableRefObject<boolean>;
  /** 是否允许 hover 打开（Copilot 为 false） */
  enableHoverOpen: boolean;
  /** 打开浮窗回调 */
  openPanel: (gameCode?: string, dashboardValue?: string, autoSelect?: boolean) => void;
  /** 当前选中游戏 code */
  selectedGameCode: string;
  /** confirm 最新引用，关闭时兜底回传 */
  confirmCurrentGameRef: React.MutableRefObject<
  (value: RegionSelectValueType, gameCode?: string) => void
  >;
  /** 选区最新引用，关闭时兜底回传 */
  localSelectValueRef: React.MutableRefObject<RegionSelectValueType>;
  /** 游戏 code 最新引用，关闭时兜底回传 */
  selectedGameCodeRef: React.MutableRefObject<string>;
}

/** useChartPopoverInteraction 返回值 */
interface IUseChartPopoverInteractionResult {
  /** 设置游戏下拉展开态（供 ChartGameSelect onOpen/onClose 同步，驱动 inert 搜索框） */
  setIsGameSelectOpen: React.Dispatch<React.SetStateAction<boolean>>;
  /** 触发器 mouseEnter：hover 模式打开浮窗 */
  handleTriggerMouseEnter: () => void;
}

/**
 * 设置或移除搜索框 inert，防止游戏下拉期间公共组件搜索框抢焦点。
 * @param input - dashboard 区域搜索 input 节点
 * @param inert - true 禁用交互，false 恢复
 */
const setSearchInputInert = (input: HTMLInputElement, inert: boolean): void => {
  if (inert) {
    input.setAttribute('inert', '');
  } else {
    input.removeAttribute('inert');
  }
};

/**
 * 管理图表浮窗的交互副作用与 hover 行为。
 *
 * - 外点关闭（useDismissOutside）
 * - 浮层内 mouseover/mousedown 检测，关闭时按需 confirm
 * - hover 离开 200ms 自动关闭（仅 hover 入口打开的场景）
 * - 游戏下拉展开期间 inert dashboard 搜索框
 *
 * @param params - 交互 Hook 入参
 * @returns 游戏下拉状态与触发器 mouseEnter 处理器
 */
const useChartPopoverInteraction = ({
  opened,
  closePanel,
  hovered,
  hoveredRef,
  enableHoverOpen,
  openPanel,
  selectedGameCode,
  confirmCurrentGameRef,
  localSelectValueRef,
  selectedGameCodeRef,
}: IUseChartPopoverInteractionParams): IUseChartPopoverInteractionResult => {
  /** 用户是否在浮窗内发生过 mousedown（关闭时决定是否 confirm） */
  const interactedRef = useRef(false);
  /** 是否通过 hover 入口打开（启用 autoClose 的前置条件） */
  const enteredRef = useRef(false);
  /** 鼠标是否位于浮层内部（驱动 autoClose） */
  const [isOverlayHovering, setIsOverlayHovering] = useState(false);
  /** 游戏下拉是否展开（驱动 inert 搜索框） */
  const [isGameSelectOpen, setIsGameSelectOpen] = useState(false);

  /** 面板外点击 / blur 关闭（含 iframe 宿主） */
  useDismissOutside({
    enabled: opened,
    onClose: closePanel,
    insideSelectors: [...CHART_POPOVER_INSIDE_SELECTORS],
  });

  /** 将 useHover 结果同步到 ref，供 handleOpenChange 同步读取 */
  useEffect(() => {
    hoveredRef.current = hovered;
  }, [hovered]);

  /**
   * 浮窗打开时挂载 document 级监听；关闭时按需 confirm 并重置交互标记。
   */
  useEffect(() => {
    if (!opened) {
      if (interactedRef.current) {
        confirmCurrentGameRef.current(
          localSelectValueRef.current,
          selectedGameCodeRef.current,
        );
      }
      setIsOverlayHovering(false);
      interactedRef.current = false;
      enteredRef.current = false;
      setIsGameSelectOpen(false);
      return;
    }

    /** 判断事件目标是否在浮窗白名单内 */
    const inInternal = (el: EventTarget | null) =>
      el instanceof Element && el.closest(CHART_POPOVER_INSIDE_CLOSEST) !== null;

    let hovering = false;
    /** 进入浮层内部 */
    const onOver = ({ target }: MouseEvent) => {
      if (!hovering && inInternal(target)) {
        hovering = true;
        setIsOverlayHovering(true);
      }
    };
    /** 离开浮层内部 */
    const onOut = ({ target, relatedTarget }: MouseEvent) => {
      if (hovering && inInternal(target) && !inInternal(relatedTarget)) {
        hovering = false;
        setIsOverlayHovering(false);
      }
    };
    /** 浮层内 mousedown 标记为已交互 */
    const onDown = ({ target }: MouseEvent) => {
      if (inInternal(target)) interactedRef.current = true;
    };

    document.addEventListener('mouseover', onOver);
    document.addEventListener('mouseout', onOut);
    document.addEventListener('mousedown', onDown, true);
    return () => {
      document.removeEventListener('mouseover', onOver);
      document.removeEventListener('mouseout', onOut);
      document.removeEventListener('mousedown', onDown, true);
    };
  }, [opened, confirmCurrentGameRef, localSelectValueRef, selectedGameCodeRef]);

  /**
   * hover 离开触发器与浮层后延迟自动关闭（对齐 Skill/Agent 按钮）。
   */
  useEffect(() => {
    if (!opened) return;
    if (hovered || isOverlayHovering || interactedRef.current) return;
    if (!enteredRef.current) return;
    const timer = window.setTimeout(closePanel, CHART_POPOVER_AUTO_CLOSE_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [hovered, isOverlayHovering, opened, closePanel]);

  /**
   * 游戏下拉展开期间 inert dashboard 搜索框，防止 `.focus()` 抢走游戏搜索焦点。
   */
  useEffect(() => {
    if (!opened || !isGameSelectOpen) return;
    let rafId = 0;
    let appliedInput: HTMLInputElement | null = null;
    /** 轮询直到搜索 input 挂载到 overlay */
    const tryInert = () => {
      const input = document.querySelector<HTMLInputElement>(
        '.dba-new-chart-popover-overlay .dbc-region-select-search-input',
      );
      if (input) {
        setSearchInputInert(input, true);
        appliedInput = input;
      } else {
        rafId = window.requestAnimationFrame(tryInert);
      }
    };
    tryInert();
    return () => {
      window.cancelAnimationFrame(rafId);
      if (appliedInput) setSearchInputInert(appliedInput, false);
    };
  }, [opened, isGameSelectOpen]);

  /**
   * 触发器 mouseEnter：标记 entered 并在未打开时调用 openPanel。
   */
  const handleTriggerMouseEnter = useCallback((): void => {
    if (!enableHoverOpen) return;
    hoveredRef.current = true;
    enteredRef.current = true;
    if (!opened) {
      openPanel(selectedGameCode, '', false);
    }
  }, [enableHoverOpen, opened, openPanel, selectedGameCode, hoveredRef]);

  return {
    setIsGameSelectOpen,
    handleTriggerMouseEnter,
  };
};

export default useChartPopoverInteraction;
